import React, { Component, Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import { bool, func, object } from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { getBuyerId, getUserType } from 'dibs-cookie-jar';
import { isEmailOnly } from 'dibs-cookie-jar/exports/isEmailOnly';
import get from 'lodash.get';
import throttle from 'lodash.throttle';
import { Button } from 'dibs-elements/exports/Button';

import { TopBar } from '../TopBar/TopBar';
import BottomBar from '../BottomBar/BottomBar';
import Drawer from '../Drawer/Drawer';

import { actionCreators } from '../../actions';
import * as userTracking from '../../utils/userTracking';
import {
    hasValidUserSessionGeoInfo,
    hasUserSessionCountryCode,
    getUserSessionCountryCode,
    getUserZipCode,
} from 'dibs-regional-info/exports/regionalInfoHelpers';
import { getLocalHistoryItemIds } from 'dibs-recent-history/exports/getLocalHistoryItemIds';
import { MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS } from 'dibs-recent-history/exports/constants';
import { TRADE } from '../../../exports/userTypeEnums';
import {
    getCurrencyPreference,
    setSessionCurrency,
    setLocalCurrency,
    getSessionCurrency,
} from '../../utils/preferredCurrency';

import { isMessageBannerClosedManually } from '../MessageBanner/helpers/sharedMessageHelpers';

import styles from './Header-style.scss';

const MESSAGE_BANNER_HEIGHT = 42;
const SCROLL_THROTTLE_DURATION = 10;

class Header extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isClient: false,
            hasError: false,
            forceSlideIn: false,
            isBannerClosedManually: false,
            isBannerOnScreen: false,
            fragmentVariables: {
                hasUserId: false,
                hasTradeUserId: false,
                userId: '',
                userIds: [],
                fetchRegionalInfo: false,
                fetchTradeNav: false,
                fetchNav: false,
                zipCode: '',
            },
        };

        this.handleTopBarMouseOver = this.handleTopBarMouseOver.bind(this);
        this.handleTopBarMouseOut = this.handleTopBarMouseOut.bind(this);
        this.setupCurrencyInfo = this.setupCurrencyInfo.bind(this);
        this.handleScroll = throttle(this.handleScroll.bind(this), SCROLL_THROTTLE_DURATION);
    }

    componentDidMount() {
        this.refetchViewer();

        const { showMarketingMessages, disableStickyNav } = this.props;
        const isBannerClosedManually = isMessageBannerClosedManually();
        const hasMessageBanner =
            showMarketingMessages && !disableStickyNav && !isBannerClosedManually;

        this.setState({ isBannerClosedManually, isBannerOnScreen: hasMessageBanner });

        if (hasMessageBanner) {
            window.addEventListener('scroll', this.handleScroll);
        }
    }

    componentDidUpdate(prevProps) {
        const { viewer, updateUserState } = this.props;
        const { user } = viewer || {};
        const { user: prevUser } = prevProps.viewer || {};

        if (updateUserState && !prevProps.updateUserState) {
            this.refetchViewer();
        }
        if (user?.isVerifiedTrade && !prevUser) {
            this.props.updateUserTypeTrade();
        }
        if (user?.isVip && !prevUser) {
            this.props.updateUserTypeVip();
        }
    }

    componentDidCatch(error) {
        if (window.newrelic) {
            const newrelicError = new Error();
            newrelicError.message = `dibs-buyer-layout header error boundary triggered: ${error.message}`;
            newrelicError.stack = error.stack;

            window.newrelic.noticeError(newrelicError);
        }

        this.setState({ hasError: true });

        this.clearScrollListener();
    }

    componentWillUnmount() {
        this.clearScrollListener();
    }

    clearScrollListener() {
        window.removeEventListener('scroll', this.handleScroll);
    }

    handleScroll() {
        const { isBannerOnScreen } = this.state;

        if (Drawer.getWindowScrollY() < MESSAGE_BANNER_HEIGHT) {
            if (!isBannerOnScreen) {
                this.setState({ isBannerOnScreen: true });
            }
        } else if (isBannerOnScreen) {
            this.setState({ isBannerOnScreen: false });
        }
    }

    getClassnames({ forceDisableStickyNav, hasTopBarOnly }) {
        const { disableStickyNav, showMarketingMessages } = this.props;
        const { isClient, isBannerClosedManually, isBannerOnScreen } = this.state;
        const disableSticky = disableStickyNav || forceDisableStickyNav;
        const hasBanner = isClient && showMarketingMessages && !isBannerClosedManually;
        const isBannerOutOfScreen = hasBanner && !disableSticky && !isBannerOnScreen;

        return {
            wrapperClasses: classnames({
                [styles.wrapper]: true,
            }),
            headerClasses: classnames({
                [styles.header]: true,
                [styles.hasBanner]: hasBanner,
                [styles.isBannerOutOfScreen]: isBannerOutOfScreen,
                [styles.disableSticky]: disableSticky,
                [styles.hasTopBarOnly]: hasTopBarOnly,
            }),
            shadowClasses: classnames({
                [styles.shadow]: true,
                [styles.disableSticky]: disableSticky,
                [styles.hasTopBarOnly]: hasTopBarOnly,
                [styles.isBannerOutOfScreen]: isBannerOutOfScreen,
            }),
            bottomWrapperClasses: classnames({
                [styles.bottomWrapper]: true,
                [styles.disableSticky]: disableSticky,
            }),
        };
    }

    /**
     * The viewer should be fetched a max of (2) times:
     *
     * (1) On componentDidMount to fetch client-only fragments, and user fragments if already logged in
     * (2) On componentDidUpdate if user logs in
     */
    refetchViewer() {
        const { isUserTypeTrade } = this.props;
        const userId = getBuyerId(document.cookie) || '';
        const cookieUserType = getUserType(document.cookie) || '';
        const hasUserId = !!userId;
        const fetchRegionalInfo =
            (!hasUserId && !getSessionCurrency()) || !hasValidUserSessionGeoInfo();
        const renderTradeNav = cookieUserType === TRADE || isUserTypeTrade;
        const hasTradeUserId = hasUserId && renderTradeNav;
        this.props.setIsEmailOnly(isEmailOnly(document.cookie));
        const checkoutUserId = userId || userTracking.getGuestUserSessionInfo().userId || '';
        const localRecentHistoryItemIds = getLocalHistoryItemIds({ isClient: true });

        const fragmentVariables = {
            ...this.state.fragmentVariables,
            hasUserId,
            hasTradeUserId,
            userId,
            userIds: [userId],
            isClient: true,
            fetchRegionalInfo,
            userCountryCode: getUserSessionCountryCode(),
            zipCode: getUserZipCode(),
            hasSessionCountryCode: hasUserSessionCountryCode(),
            localRecentHistoryItemIds,
            fetchUserRecentHistoryItem:
                localRecentHistoryItemIds.length < MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS,
            checkoutUserId,
            hasCheckoutUserId: !!checkoutUserId,
            fetchNav: !renderTradeNav,
            fetchTradeNav: renderTradeNav,
        };

        this.setState(
            {
                isClient: true,
                fragmentVariables,
            },
            () => {
                this.props.relay.refetch(
                    this.state.fragmentVariables,
                    null,
                    this.setupCurrencyInfo
                );
            }
        );
    }
    setupCurrencyInfo() {
        const { viewer } = this.props;

        const user = get(viewer, 'user');
        const userCurrency = get(user, 'profile.currency');
        const regionalCurrency = get(viewer, 'regionalInfo[0].currency');

        if (!user && !getSessionCurrency()) {
            setSessionCurrency(regionalCurrency);
        } else if (userCurrency) {
            setLocalCurrency(userCurrency);
        }

        this.props.populateCurrency(getCurrencyPreference({ user, regionalCurrency }));
    }
    handleTopBarMouseOver() {
        if (!this.state.forceSlide) {
            this.setState({ forceSlideIn: true });
        }
    }

    handleTopBarMouseOut() {
        if (this.state.forceSlide) {
            this.setState({ forceSlideIn: false });
        }
    }

    render() {
        const {
            disableStickyNav,
            viewer,
            hideHeader,
            showMarketingMessages,
            setDrawerState,
            relay,
            messageBannerRenderer,
            initialTrackingFired,
            setInitialTrackingFired,
        } = this.props;
        const user = get(viewer, 'user') || null;
        const regionalInfo = get(viewer, 'regionalInfo[0]') || null;
        const { isClient, fragmentVariables, forceSlideIn, hasError } = this.state;
        const { hasUserId, userId, fetchRegionalInfo, hasTradeUserId } = fragmentVariables;
        const forceDisableStickyNav = hasError;
        const hasUser = !!user;
        const hasRootPendingRefetch =
            (!hasUser && hasUserId) || (fetchRegionalInfo && !get(viewer, 'regionalInfo'));
        const BottomWrapperTag = disableStickyNav || forceDisableStickyNav ? 'div' : Drawer;
        const bottomWrapperProps =
            disableStickyNav || forceDisableStickyNav
                ? {}
                : { onStateChange: setDrawerState, forceSlideIn };

        const {
            wrapperClasses,
            headerClasses,
            shadowClasses,
            bottomWrapperClasses,
        } = this.getClassnames({ forceDisableStickyNav, hasTopBarOnly: hasError });

        if (hasError) {
            return (
                <header className={wrapperClasses} hidden={hideHeader}>
                    <div className={headerClasses}>
                        <TopBar
                            hasError
                            disableStickyNav
                            hasRootPendingRefetch={hasRootPendingRefetch}
                            hasUser={hasUser}
                            hasUserId={hasUserId}
                            viewer={viewer}
                            isClient={isClient}
                            userId={userId}
                            user={user}
                        />
                    </div>
                    <div className={shadowClasses} />
                </header>
            );
        }

        return (
            <header className={wrapperClasses} hidden={hideHeader}>
                <Button
                    className={styles.skipToContent}
                    type="secondaryAlt"
                    // use href to force anchor tag to render bc the button is for navigation and
                    // anchor is better semantically. prevent default to allow more control in
                    // navigation
                    href="#mainContent"
                    onClick={() => {
                        if (typeof document !== 'undefined') {
                            const mainContent = document.getElementById('mainContent');
                            const header = document.getElementById('js-header');
                            // main needs an offset bc when scrolling to, the target is aligned to top of the page, which is
                            // covered by the floating header. programmatically determine offset bc header has variable height.
                            const offset = header.offsetHeight + 'px';
                            mainContent.style.cssText = `margin-top: -${offset}; padding-top: ${offset};`;
                        }
                    }}
                >
                    <FormattedMessage
                        id="dbl.Header.SkipToContent"
                        defaultMessage="Skip to main content"
                    />
                </Button>
                <div className={headerClasses}>
                    {showMarketingMessages &&
                        messageBannerRenderer &&
                        messageBannerRenderer({
                            hasRootPendingRefetch,
                            relayEnvironment: relay.environment,
                            userId,
                            hasUserId,
                            isClient,
                            onBannerClose: () => {
                                this.setState({ isBannerClosedManually: true });

                                this.clearScrollListener();
                            },
                            hasTradeUserId,
                        })}
                    <TopBar
                        disableStickyNav={disableStickyNav}
                        hasRootPendingRefetch={hasRootPendingRefetch}
                        hasUser={hasUser}
                        hasUserId={hasUserId}
                        viewer={viewer}
                        isClient={isClient}
                        userId={userId}
                        user={user}
                        regionalInfo={regionalInfo}
                        onMouseOver={this.handleTopBarMouseOver}
                        onMouseOut={this.handleTopBarMouseOut}
                    />
                    <BottomWrapperTag className={bottomWrapperClasses} {...bottomWrapperProps}>
                        <BottomBar
                            viewer={viewer}
                            hasUserId={hasUserId}
                            hasRootPendingRefetch={hasRootPendingRefetch}
                        />
                        <div id="nav-bottom-marker" />
                    </BottomWrapperTag>
                    {viewer.relayIsClient && (
                        <Suspense fallback={''}>
                            <userTracking.UserTrackingInit
                                viewer={viewer}
                                user={user}
                                environment={relay.environment}
                                initialTrackingFired={initialTrackingFired}
                                setInitialTrackingFired={setInitialTrackingFired}
                            />
                        </Suspense>
                    )}
                </div>
                <div className={shadowClasses} />
            </header>
        );
    }
}

Header.propTypes = {
    disableStickyNav: bool,
    hideHeader: bool,
    initialTrackingFired: bool,
    isClient: bool,
    isMobile: bool,
    isUserTypeTrade: bool,
    setInitialTrackingFired: func,
    showMarketingMessages: bool,
    relay: object,
    updateUserTypeTrade: func,
    updateUserTypeVip: func,
    populateCurrency: func,
    updateUserState: bool,
    viewer: object,
    setIsEmailOnly: func.isRequired,
    setDrawerState: func,
    messageBannerRenderer: func,
};

const mapStateToProps = ({ header }) => ({
    isMobile: header.isMobile,
    disableStickyNav: header.disableStickyNav,
    hideHeader: header.hideHeader,
    updateUserState: header.updateUserState,
    initialTrackingFired: header.initialTrackingFired,
    isUserTypeTrade: header.isUserTypeTrade,
    showMarketingMessages: header.showMarketingMessages,
});

const mapDispatchToProps = dispatch => ({
    updateUserTypeTrade: () => {
        dispatch(actionCreators.setUserTypeTrade(true));
    },
    updateUserTypeVip: () => {
        dispatch(actionCreators.setUserTypeVip(true));
    },
    setIsEmailOnly: emailOnly => {
        dispatch(actionCreators.setIsEmailOnly(emailOnly));
    },
    setInitialTrackingFired: () => {
        dispatch(actionCreators.setInitialTrackingFired());
    },
    populateCurrency: currency => dispatch(actionCreators.populateCurrency(currency)),
    setDrawerState: isDrawerOpen => {
        dispatch(actionCreators.setDrawerState(isDrawerOpen));
    },
});

Header = createRefetchContainer(
    connect(mapStateToProps, mapDispatchToProps)(Header),
    {
        viewer: graphql`
            fragment Header_viewer on Viewer {
                # component's isClient and relay's $isClient is out of sync
                # this var can be used to ensure we have issued a refetch and all the fragments are in place
                relayIsClient: __typename @include(if: $isClient)
                ...TopBar_viewer @arguments(hasSessionCountryCode: $hasSessionCountryCode)
                ...userTracking_viewer @defer @include(if: $isClient)
                ...BottomBar_viewer
                user(userId: $userId) @include(if: $hasUserId) {
                    ...userTracking_user @defer
                    ...TopBar_user
                    isVip
                    isVerifiedTrade
                    profile {
                        currency
                    }
                }
                regionalInfo(userId: $userId, countryCode: $userCountryCode, zipCode: $zipCode)
                @include(if: $fetchRegionalInfo) {
                    ...userTracking_regionalInfo @relay(mask: false)
                    currency
                }
            }
        `,
    },
    graphql`
        query HeaderRefetchQuery(
            $userId: String!
            $userIds: [String]!
            $isClient: Boolean!
            $hasUserId: Boolean!
            $hasTradeUserId: Boolean!
            $fetchRegionalInfo: Boolean!
            $fetchTradeNav: Boolean!
            $fetchNav: Boolean!
            $previewKey: String = ""
            $zipCode: String!
            $userCountryCode: String = ""
            $hasSessionCountryCode: Boolean!
            $localRecentHistoryItemIds: [String] = []
            $fetchUserRecentHistoryItem: Boolean = false
            $checkoutUserId: String!
            $hasCheckoutUserId: Boolean!
        ) {
            viewer {
                ...Header_viewer
            }
        }
    `
);

export default Header;
